import { useCallback } from 'react';
import { assertExhaustive, LibraryFilterSectionKey, useHasFF } from 'venn-utils';
import type { BaseSearchParams, PrivatesSearchParams, SearchParams } from 'venn-state';
import {
  BASE_PARAMS,
  librarySearchParamsAtom,
  librarySelectedIdsAtom,
  privateLibrarySearchParams,
  privateLibrarySelectedIdsAtom,
} from 'venn-state';
import { AdvancedFilterType } from './useLibraryStateURLSynchronizer';
import type { RecoilState } from 'recoil';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { omit } from 'lodash';

type ChangedParam =
  | {
      key: 'lastTrackTime';
      value: number;
    }
  | {
      key: 'sortBy';
      column: string;
      order: 'asc' | 'desc';
    }
  | {
      key: 'page';
      value: number;
    }
  | {
      key: 'filter';
      filterName: string;
      value: string | string[];
    }
  | {
      key: 'resetSort';
    };

export const useReturnsLibraryState = () => {
  return useLibraryState<SearchParams>(librarySearchParamsAtom, librarySelectedIdsAtom);
};
export const usePrivateLibraryState = () => {
  return useLibraryState<PrivatesSearchParams>(privateLibrarySearchParams, privateLibrarySelectedIdsAtom);
};

export const useLibraryState = <StateType extends BaseSearchParams>(
  persistStateAtom: RecoilState<StateType>,
  selectionAtom: RecoilState<string[]>,
) => {
  const [librarySearchParams, setLibrarySearchParams] = useRecoilState(persistStateAtom);
  const resetSearchParams = useResetRecoilState(persistStateAtom);
  const persistSelection = useHasFF('data_library_persist_selection_ff');
  const resetSelectedIds = useResetRecoilState(selectionAtom);
  const clearSelectionOnQueryChange = useCallback(() => {
    if (!persistSelection) {
      resetSelectedIds();
    }
  }, [persistSelection, resetSelectedIds]);

  const resetLibrarySearchParams = useCallback(() => {
    clearSelectionOnQueryChange();
    resetSearchParams();
  }, [clearSelectionOnQueryChange, resetSearchParams]);

  const updateLibrarySearchParam = useCallback(
    (param: ChangedParam) => {
      clearSelectionOnQueryChange();
      // @ts-expect-error: todo fix incorrect typing
      setLibrarySearchParams((prevQueryParams) => {
        let updateParams;
        switch (param.key) {
          case 'lastTrackTime':
            // reset to be last updated
            updateParams = {
              ...prevQueryParams,
              lastTrackTime: param.value,
              sortBy: undefined,
              page: 1,
            };
            break;
          case 'sortBy':
            updateParams = {
              ...prevQueryParams,
              sortBy: param.column,
              order: param.order,
            };
            break;
          case 'resetSort':
            updateParams = {
              ...prevQueryParams,
              sortBy: undefined,
              order: 'desc',
            };
            break;
          case 'page':
            updateParams = {
              ...prevQueryParams,
              page: param.value,
            };
            break;
          case 'filter': {
            updateParams = {
              ...prevQueryParams,
              [param.filterName]: param.value,
              ...BASE_PARAMS, // Reset page and encode search string
            };
            break;
          }
          default:
            throw assertExhaustive(param);
        }

        // Remove invalidSubject paramter
        return omit(updateParams, 'invalidSubject');
      });
    },
    [setLibrarySearchParams, clearSelectionOnQueryChange],
  );

  const toggleParamItem = useCallback(
    (key: string, item: string) => {
      clearSelectionOnQueryChange();
      setLibrarySearchParams((prevQueryParams) => {
        switch (key) {
          case LibraryFilterSectionKey.FILTERS:
          case LibraryFilterSectionKey.TAGS:
          case AdvancedFilterType.CURRENCY:
          case AdvancedFilterType.ASSET_TYPE:
          case AdvancedFilterType.DATA_SOURCE:
          case AdvancedFilterType.MORNINGSTAR_CATEGORY:
          case AdvancedFilterType.TAX_STATUS:
            // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            const updated = new Set(prevQueryParams?.[key] ?? []);
            if (updated.has(item)) {
              updated.delete(item);
            } else {
              updated.add(item);
            }
            return {
              ...prevQueryParams,
              [key]: Array.from(updated),
              ...BASE_PARAMS,
            };
          default:
            return prevQueryParams;
        }
      });
    },
    [setLibrarySearchParams, clearSelectionOnQueryChange],
  );

  return {
    librarySearchParams,
    setLibrarySearchParams,
    updateLibrarySearchParam,
    resetLibrarySearchParams,
    toggleParamItem,
  };
};
